// Single-page developer docs for GG-Coin (#33; design/UX pass #273). Mostly
// static presentational components (the top nav has a mobile menu-toggle
// signal, code blocks a copied-state signal); list rendering uses <For>, JSX
// uses `class`. Copy + snippets live in ./content.ts so they unit-test without
// a DOM; the TOC constant drives the nav, the desktop rail, and the scrollspy.

import { createSignal, For, onCleanup } from "solid-js";
import {
  AGENT_GUIDE,
  API_REFERENCE,
  CONCEPTS,
  DASHBOARD_URL,
  QUICKSTART,
  SDKS,
  slugify,
  TOC,
} from "./content.ts";
import { useScrollReveal } from "./motion.ts";
import { useScrollSpy } from "./toc.ts";

function Nav() {
  const [open, setOpen] = createSignal(false);
  const close = () => setOpen(false);
  return (
    <nav
      class="nav"
      onClick={(e) => {
        // Close the mobile menu after tapping any link (but not the toggle button).
        if ((e.target as HTMLElement).closest("a")) close();
      }}
      onKeyDown={(e) => e.key === "Escape" && close()}
    >
      <a class="brand" href="/">
        <span class="brand-mark">GG</span> Docs
      </a>
      <button
        type="button"
        class="nav-toggle"
        classList={{ "is-open": open() }}
        aria-label={open() ? "Close menu" : "Open menu"}
        aria-expanded={open()}
        aria-controls="nav-menu"
        onClick={() => setOpen(!open())}
      >
        <span class="nav-toggle-bar" />
        <span class="nav-toggle-bar" />
        <span class="nav-toggle-bar" />
      </button>
      <div id="nav-menu" class="nav-links" classList={{ "is-open": open() }}>
        <For each={TOC}>{(s) => <a href={`#${s.id}`}>{s.label}</a>}</For>
        <a class="btn btn-primary" href={DASHBOARD_URL}>
          Get an API key
        </a>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <header class="hero">
      <p class="eyebrow">Developer documentation</p>
      <h1>Build on the closed-loop rail.</h1>
      <p class="lede">
        GG-Coin is a developer-first payment rail with a native agent layer. Issue a key, move coin
        in a few lines, and give autonomous agents spend-capped wallets over MCP.
      </p>
      <div class="cta-row">
        <a class="btn btn-primary" href="#quickstart">
          Start the quickstart
        </a>
        <a class="btn btn-ghost" href="#agents">
          Agent guide
        </a>
      </div>
    </header>
  );
}

/* Desktop-only side rail (#273): same TOC source as the nav, with the active
   section tracked by the scrollspy. Hidden below 1100px (the nav anchors serve
   small screens). */
function TocRail() {
  const active = useScrollSpy(TOC.map((s) => s.id));
  return (
    <aside class="toc" aria-label="On this page">
      <span class="toc-label">On this page</span>
      <For each={TOC}>
        {(s) => (
          <a
            href={`#${s.id}`}
            classList={{ "is-active": active() === s.id }}
            aria-current={active() === s.id ? "location" : undefined}
          >
            {s.label}
          </a>
        )}
      </For>
    </aside>
  );
}

/* Section heading with a hover-revealed anchor link (#273). */
function SectionHeading(props: { id: string; text: string }) {
  return (
    <h2>
      {props.text}{" "}
      <a class="h-anchor" href={`#${props.id}`} aria-label={`Link to ${props.text}`}>
        #
      </a>
    </h2>
  );
}

/* Step heading (h3) with its own slug anchor, so deep content is linkable. */
function StepHeading(props: { text: string }) {
  const slug = slugify(props.text);
  return (
    <h3 id={slug}>
      {props.text}{" "}
      <a class="h-anchor" href={`#${slug}`} aria-label={`Link to ${props.text}`}>
        #
      </a>
    </h3>
  );
}

function Concepts() {
  return (
    <section id="concepts" class="section reveal">
      <SectionHeading id="concepts" text="Concepts" />
      <div class="grid">
        <For each={CONCEPTS}>
          {(c) => (
            <div class="card">
              <h3>{c.title}</h3>
              <p>{c.body}</p>
            </div>
          )}
        </For>
      </div>
    </section>
  );
}

/** How long the copy button shows its confirmation before resetting. */
const COPY_FEEDBACK_MS = 1500;

function CodeBlock(props: { lang: string; code: string }) {
  const [copied, setCopied] = createSignal(false);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const copy = () => {
    // Clipboard writes can reject (permission denied, unfocused document,
    // unsupported API) — swallow the failure so the button never wedges and
    // no unhandled rejection escapes; the label simply stays "Copy".
    void navigator.clipboard
      ?.writeText(props.code)
      .then(() => {
        setCopied(true);
        clearTimeout(timer);
        timer = setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
      })
      .catch(() => {});
  };
  onCleanup(() => clearTimeout(timer));
  return (
    <div class="code">
      <div class="code-bar">
        <span class="code-lang">{props.lang}</span>
        <button type="button" class="code-copy" onClick={copy} aria-live="polite">
          {copied() ? "Copied" : "Copy"}
        </button>
      </div>
      <pre>
        <code>{props.code}</code>
      </pre>
    </div>
  );
}

function Quickstart() {
  return (
    <section id="quickstart" class="section reveal">
      <SectionHeading id="quickstart" text="Quickstart" />
      <p class="muted">Zero to a posted transaction.</p>
      <For each={QUICKSTART}>
        {(s) => (
          <div class="step">
            <StepHeading text={s.title} />
            <p>{s.body}</p>
            <CodeBlock lang={s.lang} code={s.code} />
          </div>
        )}
      </For>
    </section>
  );
}

function Agents() {
  return (
    <section id="agents" class="section reveal">
      <SectionHeading id="agents" text="Agent guide (MCP)" />
      <p class="muted">Give autonomous agents a spend-capped, push-approved wallet.</p>
      <For each={AGENT_GUIDE}>
        {(s) => (
          <div class="step">
            <StepHeading text={s.title} />
            <p>{s.body}</p>
            {s.code ? <CodeBlock lang={s.lang ?? "text"} code={s.code} /> : null}
          </div>
        )}
      </For>
    </section>
  );
}

function ApiReference() {
  return (
    <section id="api" class="section reveal">
      <SectionHeading id="api" text="API reference" />
      <p class="muted">The most-used endpoints — full typed surface ships in the SDK.</p>
      <div class="table-scroll">
        <table class="api-table">
          <tbody>
            <For each={API_REFERENCE}>
              {(e) => (
                <tr>
                  <td>
                    <span class={`method method-${e.method.toLowerCase()}`}>{e.method}</span>
                  </td>
                  <td>
                    <code>{e.path}</code>
                  </td>
                  <td>{e.summary}</td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Sdks() {
  return (
    <section id="sdks" class="section reveal">
      <SectionHeading id="sdks" text="SDKs" />
      <p class="muted">JavaScript first; the rest publish to their native registries.</p>
      <div class="chips">
        <For each={SDKS}>{(s) => <span class="chip">{s}</span>}</For>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer class="footer">
      <p>GG-Coin — one coin, one wallet, infinite use cases.</p>
    </footer>
  );
}

/** Root: the full single-page docs site. */
export function App() {
  useScrollReveal();
  return (
    <div class="page">
      <Nav />
      <Hero />
      <div class="layout">
        <TocRail />
        <main>
          <Concepts />
          <Quickstart />
          <Agents />
          <ApiReference />
          <Sdks />
        </main>
      </div>
      <Footer />
    </div>
  );
}
